import React from 'react';
import { Button, Tag, Tooltip, Typography, Card, message, Spin } from 'antd';
import {
  GlobalOutlined,
  TrophyOutlined,
  BarChartOutlined,
  ReloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  LinkOutlined,
  SearchOutlined,
  PaperClipOutlined,
  MailOutlined,
  MobileOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReceiverWebsite } from '../../../../types/receiver';
import { requestTrafficDataRefresh } from '../../../actions/receiver/update';
import { fetchReceiverTrafficData } from '../../../actions/receiver/fetch';
import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Text, Title } = Typography;

type WebsiteListProps = {
  websites?: ReceiverWebsite[];
  websiteUrl?: string | null;
  className?: string;
  receiverId: number;
};

const WebsiteList: React.FC<WebsiteListProps> = ({
  websites = [],
  websiteUrl,
  className = '',
  receiverId,
}) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const [refreshing, setRefreshing] = React.useState(false);

  const isLoading = useSelector(
    (state: ApplicationState) => state.receiver.receivers.trafficLoading?.[receiverId] || false
  );

  const formatVisits = (visits: number): string => {
    if (visits >= 1000000) {
      return `${(visits / 1000000).toFixed(1)}M`;
    }
    if (visits >= 1000) {
      return `${(visits / 1000).toFixed(1)}K`;
    }
    return visits.toString();
  };

  const getRankingTier = (rank: number): { color: string; tier: string } => {
    if (rank <= 1000) return { color: 'purple', tier: 'Top 1K' };
    if (rank <= 10000) return { color: 'magenta', tier: 'Top 10K' };
    if (rank <= 100000) return { color: 'gold', tier: 'Top 100K' };
    if (rank <= 1000000) return { color: 'orange', tier: 'Top 1M' };
    return { color: 'default', tier: '1M+' };
  };

  const handleRefreshTraffic = async (websiteId?: number) => {
    setRefreshing(true);
    try {
      await dispatch(
        requestTrafficDataRefresh(
          receiverId,
          websiteId ? [websiteId] : undefined,
          true // force refresh
        )
      );

      message.success(t('receiver:trafficData.refreshInitiated'));

      // Re-fetch the traffic data to get updated information
      await dispatch(fetchReceiverTrafficData(receiverId));

      message.success(t('receiver:trafficData.refreshSuccessful'));
    } catch (error) {
      message.error(t('receiver:trafficData.refreshFailed'));
    } finally {
      setRefreshing(false);
    }
  };

  let displayWebsites: ReceiverWebsite[] = [];
  if (websites.length > 0) {
    displayWebsites = websites;
  } else if (websiteUrl) {
    displayWebsites = [{ url: websiteUrl, isPrimary: true }];
  }

  const sortedWebsites = [...displayWebsites].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) {
      return a.isPrimary ? -1 : 1;
    }

    if (!a.isPrimary && !b.isPrimary) {
      if (a.trafficData?.rank && b.trafficData?.rank) {
        return a.trafficData.rank - b.trafficData.rank;
      }
      if (a.trafficData?.rank && !b.trafficData?.rank) {
        return -1;
      }
      if (!a.trafficData?.rank && b.trafficData?.rank) {
        return 1;
      }
      if (a.trafficData?.monthlyVisits && b.trafficData?.monthlyVisits) {
        return b.trafficData.monthlyVisits - a.trafficData.monthlyVisits;
      }
      if (a.trafficData?.monthlyVisits && !b.trafficData?.monthlyVisits) {
        return -1;
      }
      if (!a.trafficData?.monthlyVisits && b.trafficData?.monthlyVisits) {
        return 1;
      }
    }

    return 0;
  });

  const renderTrafficAnalysisCard = (website: ReceiverWebsite) => {
    const { trafficData } = website;
    if (!trafficData) return null;

    return (
      <Card
        className="mt-3"
        size="small"
        title={
          <div className="flex items-center justify-between">
            <Title level={5} className="mb-0 text-gray-700">
              <BarChartOutlined className="mr-2 text-blue-500" />
              {t('receiver:trafficData.siteTrafficAnalysis')}
            </Title>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              size="small"
              loading={isLoading || refreshing}
              onClick={() => handleRefreshTraffic(website.id)}
              title={t('receiver:trafficData.refreshTrafficData')}
            />
          </div>
        }
      >
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <GlobalOutlined className="mr-2 text-blue-500" />
            <Button
              type="link"
              href={`${website.url.includes('//') ? '' : '//'}${website.url}`}
              target="_blank"
              className="p-0 h-auto text-blue-600"
              rel="noopener noreferrer"
            >
              {website.url}
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <Text strong className="block mb-2 text-gray-600">
            Monthly Visits
          </Text>

          {trafficData.monthlyData && (
            <div className="space-y-1">
              {Object.entries(trafficData.monthlyData)
                .sort(([a], [b]) => a.localeCompare(b))
                .slice(-3)
                .map(([month, visits]) => (
                  <div key={month} className="flex items-center justify-between py-1">
                    <div className="flex items-center">
                      <EyeOutlined className="mr-2 text-blue-500" />
                      <Text className="text-sm">
                        {new Date(month).toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Text>
                    </div>
                    <Text strong className="text-blue-600 text-sm">
                      {formatVisits(visits)}
                    </Text>
                  </div>
                ))}
            </div>
          )}
          {!trafficData.monthlyData && trafficData.monthlyVisits && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center">
                <EyeOutlined className="mr-2 text-blue-500" />
                <Text className="text-sm">{t('receiver:trafficData.currentMonth')}</Text>
              </div>
              <Text strong className="text-blue-600 text-sm">
                {formatVisits(trafficData.monthlyVisits)}
              </Text>
            </div>
          )}
        </div>

        {trafficData.monthlyVisits && (
          <div className="bg-blue-50 p-3 rounded mb-4 border-l-4 border-blue-400">
            <Text className="block text-center text-gray-600 text-xs mb-1">
              {t('receiver:trafficData.latestMonthTotal')}
            </Text>
            <div className="text-center">
              <Text strong className="text-xl text-blue-600">
                {formatVisits(trafficData.monthlyVisits)}
              </Text>
            </div>
          </div>
        )}

        <div className="mb-4">
          <Text strong className="block mb-2 text-gray-600">
            {t('receiver:trafficData.trafficSources')}
          </Text>

          {trafficData.trafficSources && (
            <div className="space-y-1">
              {Object.entries(trafficData.trafficSources)
                .filter(([, percentage]) => percentage !== undefined && percentage !== null)
                .map(([source, percentage]) => {
                  const getSourceIcon = (sourceName: string) => {
                    switch (sourceName.toLowerCase()) {
                      case 'direct':
                        return { color: 'text-green-500', Icon: LinkOutlined };
                      case 'search':
                        return { color: 'text-blue-500', Icon: SearchOutlined };
                      case 'referrals':
                        return { color: 'text-orange-500', Icon: PaperClipOutlined };
                      case 'mail':
                        return { color: 'text-red-500', Icon: MailOutlined };
                      case 'social':
                        return { color: 'text-purple-500', Icon: MobileOutlined };
                      case 'paid referrals':
                        return { color: 'text-yellow-500', Icon: DollarOutlined };
                      default:
                        return { color: 'text-gray-500', Icon: GlobalOutlined };
                    }
                  };

                  const sourceInfo = getSourceIcon(source);
                  const SourceIcon = sourceInfo.Icon;

                  return (
                    <div key={source} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <SourceIcon className={`mr-2 ${sourceInfo.color}`} />
                        <Text className="text-sm">{source}</Text>
                      </div>
                      <Text className="text-gray-500 text-sm">
                        {(percentage! * 100).toFixed(1)}%
                      </Text>
                    </div>
                  );
                })}
            </div>
          )}
          {!trafficData.trafficSources && trafficData.topCountries && (
            <div className="space-y-1">
              <Text type="secondary" className="block mb-2 text-xs">
                Top Countries by Traffic Share
              </Text>
              {trafficData.topCountries.slice(0, 5).map((country, index) => (
                <div key={country.CountryCode} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className={`w-2 h-2 bg-blue-500 rounded-full mr-2 opacity-${
                        100 - index * 20
                      }`}
                    />
                    <Text className="text-sm">{country.CountryCode}</Text>
                  </div>
                  <Text className="text-gray-500 text-sm">{(country.Value * 100).toFixed(1)}%</Text>
                </div>
              ))}
            </div>
          )}
          {!trafficData.trafficSources && !trafficData.topCountries && (
            <div className="text-center py-2">
              <Text type="secondary" className="text-xs">
                {t('receiver:trafficData.trafficSourceDataNotAvailable')}
              </Text>
            </div>
          )}
        </div>

        <div className="border-t pt-2 flex items-center justify-between">
          {trafficData.lastUpdated && (
            <Text type="secondary" className="text-xs">
              {t('receiver:trafficData.lastUpdated')}:{' '}
              {new Date(trafficData.lastUpdated).toLocaleDateString()}{' '}
              {new Date(trafficData.lastUpdated).toLocaleTimeString()}
            </Text>
          )}

          {trafficData.rank && (
            <Tag color="blue" className="text-xs ml-auto">
              {t('receiver:trafficData.globalRank')}: #{trafficData.rank.toLocaleString()}
            </Tag>
          )}
        </div>
      </Card>
    );
  };

  const noInformation = () => <Text type="secondary">{t('receiver:noInformation')}</Text>;

  return (
    <div className={className}>
      <Spin spinning={isLoading || refreshing} indicator={<LoadingOutlined spin />}>
        {sortedWebsites.length > 0 ? (
          <div className="space-y-4">
            {sortedWebsites.map((website, index) => {
              let rankPosition: number | undefined;
              if (
                !website.isPrimary &&
                (website.trafficData?.rank || website.trafficData?.monthlyVisits)
              ) {
                const nonPrimaryWithTraffic = sortedWebsites.filter(
                  w => !w.isPrimary && (w.trafficData?.rank || w.trafficData?.monthlyVisits)
                );
                if (nonPrimaryWithTraffic.length > 1) {
                  rankPosition = nonPrimaryWithTraffic.findIndex(w => w.url === website.url) + 1;
                }
              }

              return (
                <div key={index} className="website-item">
                  <div className="flex items-center space-x-2 mb-2">
                    {!website.isPrimary &&
                      website.trafficData &&
                      (website.trafficData.rank || website.trafficData.monthlyVisits) &&
                      rankPosition && (
                        <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-xs font-bold">
                          {rankPosition}
                        </div>
                      )}

                    <Button
                      href={`${website.url.includes('//') ? '' : '//'}${website.url}`}
                      target="_blank"
                      type="link"
                      rel="noopener noreferrer"
                      icon={<GlobalOutlined />}
                      className="p-0"
                    >
                      {website.url}
                    </Button>

                    {website.isPrimary && (
                      <Tag color="blue" className="text-xs">
                        {t('receiver:trafficData.primary')}
                      </Tag>
                    )}

                    {website.trafficData?.monthlyVisits && (
                      <Tooltip
                        title={`${website.trafficData.monthlyVisits.toLocaleString()} ${t(
                          'receiver:trafficData.monthlyVisits'
                        )}`}
                      >
                        <Tag icon={<BarChartOutlined />} color="green" className="text-xs">
                          {formatVisits(website.trafficData.monthlyVisits)}
                        </Tag>
                      </Tooltip>
                    )}

                    {website.trafficData?.rank && (
                      <Tooltip
                        title={`${t(
                          'receiver:trafficData.globalRanking'
                        )}: ${website.trafficData.rank.toLocaleString()}`}
                      >
                        <Tag
                          icon={<TrophyOutlined />}
                          color={getRankingTier(website.trafficData.rank).color}
                          className="text-xs"
                        >
                          {getRankingTier(website.trafficData.rank).tier}
                        </Tag>
                      </Tooltip>
                    )}
                  </div>

                  {website.trafficData && renderTrafficAnalysisCard(website)}
                </div>
              );
            })}

            {sortedWebsites.some(w => w.trafficData?.lastUpdated) && (
              <div className="mt-4 p-2 bg-green-50 rounded border-l-4 border-green-400">
                <Text type="secondary" className="text-xs block">
                  <CheckCircleOutlined className="text-green-500 mr-1" />{' '}
                  <strong>{t('receiver:trafficData.trafficAnalytics')}:</strong>{' '}
                  {t('receiver:trafficData.dataUpdated')} {new Date().toLocaleDateString()}
                </Text>
                <Text type="secondary" className="text-xs">
                  {t('receiver:trafficData.rankingSystemActive')} •{' '}
                  {t('receiver:trafficData.monthlyRefreshEnabled')}
                </Text>
              </div>
            )}
          </div>
        ) : (
          noInformation()
        )}
      </Spin>
    </div>
  );
};

export default WebsiteList;
